import { useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import { Checkbox, ConfigProvider } from "antd"
import { useUpdateFurnitureStatus } from "hooks/api/quotation/quotations.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { spliceFurnitureIntoCache } from "helpers/quotationCache.helper"
import AnnotationService from "services/annotations.service"
import { FurnitureModelLabel } from "components/ui/customWebviewer/FurnitureModelLabel/FurnitureModelLabel"
import { getFurnitureDisplayLabel } from "components/ui/customWebviewer/FurnitureModelLabel/furnitureModel.helpers"
import { FurnitureStatusIcon } from "components/ui/customWebviewer/ElevationReferencesSidebar/icons/FurnitureStatusIcon"
import { ModelNumberMismatchIndicator } from "components/ui/customWebviewer/shared/ModelNumberMismatchIndicator/ModelNumberMismatchIndicator"
import {
	FURNITURE_MISMATCH_MESSAGE,
	countMismatches,
	formatMismatchCount,
} from "components/ui/customWebviewer/shared/ModelNumberMismatchIndicator/modelNumberMismatch.helpers"
import { TriangleAlertSmallIcon } from "components/ui/icons/Warning/TriangleAlertSmallIcon"
import { UnlinkIcon } from "components/ui/icons/unlink/UnlinkIcon"
import { ConfirmModal } from "components/ui/modals/ConfirmModal/ConfirmModal"
import type { TErefWithFurniture, TFurniture, TFurnitureStatus } from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import style from "./Objects.module.scss"

const { objects, label, mismatchSummary, mismatchSummaryIcon, checkboxGroup, checkboxItem, rowButton, rowActions, detachButton } =
	style

const EMPTY_IDS: ReadonlySet<number> = new Set()

type Props = {
	eref: TErefWithFurniture
	onFurnitureClick?: (furniture: TFurniture) => void
	onDetachFurniture?: (furniture: TFurniture) => Promise<void>
	/** REQ-250: completed cabinets whose model number has no price for their series. */
	mismatchedFurnitureIds?: ReadonlySet<number>
}

export const Objects = ({ eref, onFurnitureClick, onDetachFurniture, mismatchedFurnitureIds = EMPTY_IDS }: Props) => {
	const [searchParams] = useSearchParams()
	const quotationId = Number(searchParams.get("quotationID"))
	const queryClient = useQueryClient()
	const { formatError } = useApiErrorFormatter()
	const { mutate: updateFurnitureStatus, isPending, variables } = useUpdateFurnitureStatus()
	const pendingFurnitureId = isPending ? variables?.furnitureId : null
	const [detachTarget, setDetachTarget] = useState<TFurniture | null>(null)
	const [isDetaching, setIsDetaching] = useState(false)

	const confirmDetach = async () => {
		if (!detachTarget || !onDetachFurniture) return
		setIsDetaching(true)
		try {
			await onDetachFurniture(detachTarget)
		} catch {
			// Error toast is surfaced by the detach handler; just close the dialog.
		} finally {
			setIsDetaching(false)
			setDetachTarget(null)
		}
	}

	if (!eref.furniture.length) return null

	const mismatchCount = countMismatches(eref.furniture, mismatchedFurnitureIds)

	const toggleQuotation = (item: TFurniture) => {
		if (!Number.isFinite(quotationId) || !quotationId) return
		const currentStatus: TFurnitureStatus = item.status ?? "work_in_progress"
		const next: TFurnitureStatus = currentStatus === "in_quotation" ? "reviewed" : "in_quotation"

		updateFurnitureStatus(
			{
				quotationId,
				furnitureId: item.id,
				body: { version: item.version, status: next },
			},
			{
				onSuccess: (response) => {
					const updated = response?.data as TFurniture | undefined
					if (!updated) return
					queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) =>
						spliceFurnitureIntoCache(cached, updated),
					)
					AnnotationService.updateFurnitureEntityStatus(item.id, next)
				},
				onError: (e) => {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
				},
			},
		)
	}

	return (
		<div className={objects}>
			<span className={label}>Objects</span>
			{mismatchCount > 0 ? (
				<p className={mismatchSummary}>
					<TriangleAlertSmallIcon className={mismatchSummaryIcon} />
					{formatMismatchCount(mismatchCount)}
				</p>
			) : null}
			<div className={checkboxGroup}>
				{eref.furniture.map((item) => {
					const status: TFurnitureStatus = item.status ?? "work_in_progress"
					return (
						<div key={item.id} className={checkboxItem}>
							<button type="button" className={rowButton} onClick={() => onFurnitureClick?.(item)}>
								<FurnitureStatusIcon status={status} />
								<FurnitureModelLabel item={item} />
								{mismatchedFurnitureIds.has(item.id) ? (
									<ModelNumberMismatchIndicator message={FURNITURE_MISMATCH_MESSAGE} />
								) : null}
							</button>
							<div className={rowActions}>
								{onDetachFurniture && (
									<button
										type="button"
										className={detachButton}
										onClick={() => setDetachTarget(item)}
										aria-label={`Detach ${getFurnitureDisplayLabel(item)}`}
									>
										<UnlinkIcon />
									</button>
								)}
								{/* Scoped green checkbox (mirrors --clr-quotationGreen); Ant theme tokens require a literal color */}
								<ConfigProvider theme={{ token: { colorPrimary: "#00A651" } }}>
									<Checkbox
										checked={status === "in_quotation"}
										disabled={pendingFurnitureId === item.id}
										onChange={() => toggleQuotation(item)}
									/>
								</ConfigProvider>
							</div>
						</div>
					)
				})}
			</div>
			<ConfirmModal
				isOpen={detachTarget !== null}
				onClose={() => setDetachTarget(null)}
				onConfirm={confirmDetach}
				title={detachTarget ? `Detach object ${getFurnitureDisplayLabel(detachTarget)}?` : ""}
				description={
					detachTarget
						? `Object ${getFurnitureDisplayLabel(detachTarget)} will be detached from elevation reference. You can reconnect it later.`
						: ""
				}
				confirmLabel="Detach"
				isPending={isDetaching}
			/>
		</div>
	)
}
